import { degToRad } from '../util';
import { nthLabels } from './util';
import {
  LINE_HEIGHT,
  LABEL_FONT_SIZE,
  TICK_PADDING,
  TITLE_PADDING,
  MARGIN_PADDING,
} from './constants';

/**
 * Given an array of labels, the angle the labels are rotated, and the margins
 * to the bottom/left/right of the axis, calculate an array of labels to be
 * displayed, as well as their widths.
 *
 * If there are too many labels to show in the given space, this will return
 * every nth label, where n is the number of labels that can fit adjacent to
 * each other without overlap.
 *
 *
 * To calculate the label width:
 * * Look at the first two labels in the axis. Since all labels are equidistant,
 *   we can just look at the first two
 * * If the angle is less than 90deg (meaning the labels are not perpendicular
 *   to the axis)
 *   * Calculate the max length of a label using basic trig. The label width is
 *     the hypoteneuse, the opposite side length is the bottom margin, and we
 *     know the angle
 * * Else
 *   * The maximum length of a label is the distance from the axis to the edge
 *     of the chart, after acocunting for any padding
 */
export function angledLabels({
  labels,
  angle,
  bottomMargin,
  leftMargin,
  showTitle,
}) {
  let n = 1;
  if (labels.length > 1) {
    const [first, second] = labels;

    // Since every label will only be one line, we just need to get the height
    // of the first label, since it's the same for all other labels
    let labelSpace = LABEL_FONT_SIZE * LINE_HEIGHT;
    let spaceBetweenPoints = Math.abs(second.position - first.position);

    if (angle < 90) {
      labelSpace *= Math.cos(degToRad(angle));
      spaceBetweenPoints /= 2;
    }

    // We only need to show every nth label if there is not enough space between a
    // single-line label between adjacent points along the axis
    n = Math.ceil(labelSpace / spaceBetweenPoints);
  }

  let usableSpace = showTitle
    ? bottomMargin - TICK_PADDING - TITLE_PADDING
    : bottomMargin - TICK_PADDING - MARGIN_PADDING;

  let labelWidth = usableSpace;
  if (angle < 90) labelWidth /= Math.sin(degToRad(angle));

  const displayedLabels = [];
  nthLabels(labels, n, ({ current }) => {
    // Make sure that the label doesn't flow off the edge of the chart
    if (angle < 90) {
      const spaceUntilChartEdge = leftMargin + current.position - 10;
      const maximumWidthUntilEdge =
        spaceUntilChartEdge / Math.cos(degToRad(angle));
      current.width = Math.min(maximumWidthUntilEdge, labelWidth);
    } else {
      current.width = labelWidth;
    }

    displayedLabels.push(current);
  });

  return displayedLabels;
}
